import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {
  JackingFieldTemplateEditor,
  JackingEventRowEditor,
  JackingFieldValueEditor,
  JackingFormInstanceEditor
} from '../../../../../models/jacking-v2/jacking.models';
import {PtLuxonDate} from '../../../../../models/projects/date.models';
import {ToastController} from 'ionic-angular';
import {DateService} from '../../../../../services/date/date.service';

@Component({
  selector: 'jacking-field',
  templateUrl: 'jacking-field.component.html'
})
export class JackingFieldComponent implements OnChanges {
  @Input() field: JackingFieldTemplateEditor;
  @Input() editor: JackingFormInstanceEditor;
  @Input() isReadOnly: boolean;
  @Input() activitySpansMultipleDays: boolean = false;

  // only used for decimal inputs so users can type intermediate values like '-', '.', '-.'
  public decimalText = '';
  private lastFieldId: number | null = null;

  public constructor(private toastController: ToastController,
                     private dateService: DateService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.field) { return; }

    // this component instance should represent one field, but keep this safe
    if (this.field.id !== this.lastFieldId) {
      this.lastFieldId = this.field.id;

      if (this.field.dataTypeId === 3) {
        const v = this.getValue();
        this.decimalText = v == null ? '' : String(v);
      }
    }
  }

  isComputed(): boolean {
    return !!this.field?.dataKey;
  }

  isDateTimeField(): boolean {
    return this.field?.dataTypeId === 4 || this.field?.dataTypeId === 5;
  }

  getDisplayLabel(): string {
    return this.isAbortTimeField() ? 'Abort Time' : this.field?.label;
  }

  getDateTimeField(): JackingFieldTemplateEditor {
    if (!this.isAbortTimeField()) {
      return this.field;
    }

    return {
      ...this.field,
      readOnly: true,
      requiresConfirmation: false
    };
  }

  // case-insensitive dataByKey lookup
  getDataValue(): any {
    if (!this.field?.dataKey || !this.editor?.dataByKey) {
      return null;
    }

    const key = this.field.dataKey;
    const exact = this.editor.dataByKey[key];
    if (exact !== undefined && exact !== null) {
      return exact;
    }

    const lowerKey = key.toLowerCase();
    const lower = this.editor.dataByKey[lowerKey];
    if (lower !== undefined && lower !== null) {
      return lower;
    }

    const match = Object.keys(this.editor.dataByKey).find(k => k.toLowerCase() === lowerKey);
    return match ? this.editor.dataByKey[match] : null;
  }

  // Reads a stored value without mutating the editor just because the field was rendered.
  getStored(): JackingFieldValueEditor {
    if (!this.editor?.valuesByFieldId) {
      return {fieldTemplateId: this.field.id};
    }

    const existing = this.editor.valuesByFieldId[this.field.id];
    if (existing) {
      return existing;
    }

    return {fieldTemplateId: this.field.id};
  }

  getOptions() {
    if (!this.field?.optionsKey || !this.editor?.optionsByKey) {
      return [];
    }

    const key = this.field.optionsKey;
    const dict = this.editor.optionsByKey;

    const exact = dict[key];
    if (exact) {
      return exact;
    }

    const lowerKey = key.toLowerCase();
    const lower = dict[lowerKey];
    if (lower) {
      return lower;
    }

    const match = Object.keys(dict).find(k => k.toLowerCase() === lowerKey);
    return match ? dict[match] : [];
  }

  // single “effective value” getter for each underlying type
  getValue() {
    if (this.isAbortTimeField()) {
      return this.getAbortTimeDisplayValue();
    }

    if (this.isComputed()) {
      return this.getDataValue();
    }

    const v = this.getStored();
    switch (this.field.dataTypeId) {
      case 1:
        return v.stringValue;
      case 2:
        return v.intValue;
      case 3:
        return v.decimalValue;
      case 4:
      case 5:
        return v.dateTimeValue;
      case 6:
        return v.intValue; // dropdown id
      case 7:
        return v.boolValue;
      default:
        return null;
    }
  }

  getDisplayOffsetHours(): number | null {
    if (this.isAbortTimeField()) {
      return this.editor?.cancellationTimeOffset ?? null;
    }

    return this.getStored()?.timeZoneOffsetValue ?? null;
  }

  isDisplayReadOnly(): boolean {
    return this.field?.readOnly || this.isComputed() || this.isReadOnly || this.isAbortTimeField();
  }

  setValue(val: any) {
    if (this.isComputed()) {
      return;
    }

    const v = this.getWritableStored();
    switch (this.field.dataTypeId) {
      case 1:
        v.stringValue = val;
        break;
      case 2:
        v.intValue = val !== '' && val !== null ? Number(val) : null;
        break;
      case 3: {
        if (val === '' || val === null || val === undefined) {
          v.decimalValue = null;
          break;
        }
        const n = Number(val);
        v.decimalValue = Number.isNaN(n) ? null : n;
        break;
      }
      case 6:
        v.intValue = val !== '' && val !== null ? Number(val) : null;
        break;
      case 7:
        v.boolValue = !!val;
        break;
    }
  }

  getStep() {
    if (this.field.dataTypeId !== 3) {
      return null;
    }
    const dp = this.field.decimalPlaces ?? 2;
    if (dp <= 0) {
      return '1';
    }
    return '0.' + '0'.repeat(dp - 1) + '1';
  }

  getSelectedOption() {
    const id = this.getValue();
    if (id === null || id === undefined) {
      return null;
    }
    return (this.getOptions() || []).find(o => o.id === Number(id)) || null;
  }

  onSelectableChange(opt: any) {
    // ionic-selectable gives back the selected item object
    this.setValue(opt ? opt.id : null);
  }

  // ngModelChange handler for decimal input
  onDecimalTextChange(val: any): void {
    if (this.field?.readOnly || this.isComputed() || this.isReadOnly) {
      return;
    }

    const text = val == null ? '' : String(val);
    this.decimalText = text;

    const trimmed = text.trim();

    // allow in-progress states without committing to the model
    if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') {
      if (trimmed === '') { this.setValue(null); }
      return;
    }

    const allowNegative = this.field.minValue == null || this.field.minValue < 0;
    const decimals = this.field.decimalPlaces ?? 2;

    if (!this.isValidDecimal(trimmed, decimals, allowNegative)) {
      return;
    }

    const n = Number(trimmed);
    if (Number.isNaN(n)) {
      return;
    }

    // commit whenever the text is a real number
    this.setValue(n);
  }

  onDecimalKeyDown(event: KeyboardEvent, decimals: number, allowNegative = false) {
    // don’t block navigation / editing / shortcuts
    if (this.isEditingKey(event)) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const key = event.key;

    // allow a single leading '-' if enabled
    if (allowNegative && key === '-') {
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const isReplacingAll = start === 0 && end === (input.value?.length ?? 0);

      if ((start === 0 || isReplacingAll) && !input.value.includes('-')) {
        return;
      }
      event.preventDefault();
      return;
    }

    // only allow digits or '.'
    const isDigit = key >= '0' && key <= '9';
    const isDot = key === '.';

    if (!isDigit && !isDot) {
      event.preventDefault();
      return;
    }

    // no decimals allowed
    if (decimals <= 0 && isDot) {
      event.preventDefault();
      return;
    }

    // compute what the value would become if we accept this key
    const next = this.applyKey(input.value, key, input.selectionStart, input.selectionEnd);

    if (!this.isValidDecimal(next, decimals, allowNegative)) {
      event.preventDefault();
    }
  }

  onDecimalPaste(event: ClipboardEvent, decimals: number, allowNegative = false) {
    const input = event.target as HTMLInputElement;
    const paste = (event.clipboardData?.getData('text') ?? '').trim();

    // compute next value if paste allowed
    const next = this.applyPaste(input.value, paste, input.selectionStart, input.selectionEnd);

    if (!this.isValidDecimal(next, decimals, allowNegative)) {
      event.preventDefault();
    }
  }

  private isEditingKey(event: KeyboardEvent): boolean {
    const k = event.key;

    // navigation/edit keys
    if (
      k === 'Backspace' || k === 'Delete' || k === 'Tab' || k === 'Enter' ||
      k === 'Escape' || k === 'Home' || k === 'End' ||
      k === 'ArrowLeft' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowDown'
    ) {
      return true;
    }

    // ctrl/cmd shortcuts
    if (event.ctrlKey || event.metaKey) {
      const kk = k.toLowerCase();
      if (kk === 'a' || kk === 'c' || kk === 'v' || kk === 'x') {
        return true;
      }
    }

    return false;
  }

  private applyKey(value: string, key: string, start: number | null, end: number | null): string {
    const s = start ?? value.length;
    const e = end ?? value.length;
    return value.slice(0, s) + key + value.slice(e);
  }

  private applyPaste(value: string, paste: string, start: number | null, end: number | null): string {
    const s = start ?? value.length;
    const e = end ?? value.length;
    return value.slice(0, s) + paste + value.slice(e);
  }

  private isValidDecimal(val: string, decimals: number, allowNegative: boolean): boolean {
    if (val === '') {
      return true;
    }

    // allow just "-" while typing
    if (allowNegative && val === '-') {
      return true;
    }

    const re = allowNegative
      ? new RegExp(`^-?\\d*(\\.\\d{0,${decimals}})?$`)
      : new RegExp(`^\\d*(\\.\\d{0,${decimals}})?$`);

    if (!re.test(val)) {
      return false;
    }

    // block second dot explicitly
    const dotCount = (val.match(/\./g) || []).length;
    if (dotCount > 1) {
      return false;
    }

    // enforce decimals length
    const parts = val.split('.');
    if (parts.length === 2 && parts[1].length > decimals) {
      return false;
    }

    return true;
  }

  async onDateTimeValueChanged(e: { valueUtc: PtLuxonDate | null; offsetHours: number | null; confirmed: boolean | null; dateChanged?: boolean }) {
    if (this.isComputed()) {
      // warn so this is caught immediately during testing
      this.toastController.create({
        message: 'Error Detected - Field should not be editable - contact system administrator',
        duration: 4000,
        cssClass: 'toast-danger',
        position: 'middle'
      }).present();
      return;
    }

    const stored = this.getWritableStored();
    stored.dateTimeValue = e.valueUtc;
    stored.timeZoneOffsetValue = e.offsetHours;
    stored.boolValue = e.confirmed;

    // legs free field IS the op end time for departures
    // need to update here, as events multi-day logic hangs off of it
    await this.applyOperationEndTimeFromLegsFreeTime(stored, !!e.dateChanged);
  }

  showEstimatedTimeTip(): boolean {
    if (!this.field || !this.editor) {
      return false;
    }

    // identify the field via template metadata
    if (this.field.dataKey !== 'StartDate') {
      return false;
    }

    // only show the tip if the op start jacking event has no date
    const opStartEvent = this.editor.jackingEvents.eventRows.find(e => e.linkTargetType === 'operationStart');
    if (opStartEvent != null && (opStartEvent.eventDate == null || opStartEvent.eventDate.dateTime == null)) {
      return true;
    }
  }

  isAbortTimeField(): boolean {
    if (!this.field || !this.editor?.cancellationTime?.dateTime) {
      return false;
    }

    const label = (this.field.label || '').trim().toLowerCase();
    return this.field.dataKey === 'EndDate' || label === 'legs free time';
  }

  private getAbortTimeDisplayValue(): string {
    const cancellationTime = this.editor?.cancellationTime
      ? PtLuxonDate.assignPtLuxonDate(this.editor.cancellationTime as any)
      : null;

    const dtLocal = cancellationTime?.dateTime;
    if (!dtLocal) {
      return '';
    }

    const offsetHours = this.editor?.cancellationTimeOffset ?? (dtLocal.offset / 60);
    const offsetLabel = `(${offsetHours >= 0 ? '+' : ''}${offsetHours})`;
    return `${dtLocal.toFormat('dd/LL/yyyy, HH:mm')} ${offsetLabel}`;
  }

  // Syncs departure Legs Free Time into the editor end time and applies the automatic next-day rule.
  private async applyOperationEndTimeFromLegsFreeTime(stored: JackingFieldValueEditor, dateChanged: boolean): Promise<void> {
    if (!this.field || !this.editor) {
      return;
    }

    const isLegsFreeTime = (this.field.label || '').trim().toLowerCase() === 'legs free time';

    if (!isLegsFreeTime) {
      return;
    }

    if (!stored.dateTimeValue?.dateTime) {
      this.editor.endDate = null;
      this.editor.endOffset = stored.timeZoneOffsetValue ?? 0;
      return;
    }

    let localEndDate = this.buildLocalPtFromStored(stored);
    const latestEventTime = this.getLatestEventTimeForLegsFreeCheck();

    if (!dateChanged
      && latestEventTime?.dateTime
      && this.dateService.isFirstDateLargerOffsetsPreApplied(latestEventTime.dateTime, localEndDate.dateTime)) {
      localEndDate = await this.dateService.incrementDay(localEndDate);
      stored.dateTimeValue = this.toUtcStored(localEndDate);
      stored.timeZoneOffsetValue = localEndDate.dateTime.offset / 60;
    }

    this.editor.endDate = localEndDate;
    this.editor.endOffset = stored.timeZoneOffsetValue ?? (localEndDate.dateTime.offset / 60);
  }

  private getWritableStored(): JackingFieldValueEditor {
    this.editor.valuesByFieldId = this.editor.valuesByFieldId || {};
    const existing = this.editor.valuesByFieldId[this.field.id];
    if (existing) {
      return existing;
    }

    const stored: JackingFieldValueEditor = {fieldTemplateId: this.field.id};
    this.editor.valuesByFieldId[this.field.id] = stored;
    return stored;
  }

  // Rebuilds a local PtLuxonDate from the stored UTC value and offset.
  private buildLocalPtFromStored(stored: JackingFieldValueEditor): PtLuxonDate {
    const pt = new PtLuxonDate();
    pt.dateTime = stored.dateTimeValue.dateTime.toUTC();

    if (stored.timeZoneOffsetValue != null) {
      pt.setOffset(stored.timeZoneOffsetValue * 3600, false);
    }

    return pt;
  }

  // Converts a local PtLuxonDate back into the UTC shape stored on the field value.
  private toUtcStored(ptLocal: PtLuxonDate): PtLuxonDate {
    const utc = new PtLuxonDate();
    utc.dateTime = ptLocal.dateTime.toUTC();
    return utc;
  }

  // Returns the latest event time that should influence the automatic Legs Free next-day bump.
  private getLatestEventTimeForLegsFreeCheck(): PtLuxonDate {
    const operationStartEvent = (this.editor?.jackingEvents?.eventRows || [])
      .find((row: JackingEventRowEditor) => row.linkTargetType === 'operationStart' && row.eventDate?.dateTime);

    let latestDate = operationStartEvent?.eventDate || this.editor.startDate;

    for (const event of this.editor?.jackingEvents?.eventRows || []) {
      if (!event?.eventDate?.dateTime || event.canBeAfterEndTime) {
        continue;
      }

      if (this.dateService.isFirstDateLargerOffsetsPreApplied(event.eventDate.dateTime, latestDate.dateTime)) {
        latestDate = event.eventDate;
      }
    }

    return latestDate;
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/components/jacking-field-component/jacking-field.component.ts